/**
 * ReviewerPersonaEditor Component
 *
 * Full editor for creating/editing therapy_reviewer personas.
 * Allows configuration of:
 * - Basic info (key, name, description)
 * - System prompt (modality-specific review criteria)
 * - Evaluation dimensions (custom scoring dimensions with weights)
 * - Output configuration (excerpts, max suggestions)
 * - AI Prompt Assistant for generating/refining reviewer personas
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Card,
  IconButton,
  FormControlLabel,
  Switch,
  Divider,
  Alert,
  Slider,
  Tooltip,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Add as AddIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Help as HelpIcon
} from '@mui/icons-material';
import EvaluationDimensionEditor from './EvaluationDimensionEditor';
import ReviewerPromptAssistant from './ReviewerPromptAssistant';

const DEFAULT_DIMENSION = {
  name: '',
  weight: 1.0,
  description: '',
  scoringCriteria: '0=Absent, 1=Basic, 2=Good, 3=Excellent',
  maxScore: 3
};

const DEFAULT_SYSTEM_PROMPT = `You are an expert clinical supervisor reviewing AI therapy simulation transcripts.
Your goal is to identify specific, actionable improvements to the therapist's system prompt
that will improve therapeutic effectiveness, safety, and client engagement.

## Your Role
Evaluate the therapist's performance based on the following criteria.

## Evaluation Criteria
[Add your modality-specific criteria here]

## Output Format
Return JSON with scores, evidence excerpts, and improvement suggestions.`;

function ReviewerPersonaEditor({ persona, onSave, onCancel, isLoading = false, token }) {
  // Basic info
  const [key, setKey] = useState(persona?.key || '');
  const [name, setName] = useState(persona?.name || '');
  const [description, setDescription] = useState(persona?.description || '');

  // System prompt (contains modality-specific criteria)
  const [systemPrompt, setSystemPrompt] = useState(persona?.system_prompt || DEFAULT_SYSTEM_PROMPT);

  // Evaluation dimensions
  const [dimensions, setDimensions] = useState(
    persona?.reviewConfiguration?.evaluationDimensions?.length > 0
      ? persona.reviewConfiguration.evaluationDimensions
      : [{ ...DEFAULT_DIMENSION }]
  );

  // Output config
  const [includeExcerpts, setIncludeExcerpts] = useState(
    persona?.reviewConfiguration?.outputFormat?.includeTranscriptExcerpts ?? true
  );
  const [maxSuggestions, setMaxSuggestions] = useState(
    persona?.reviewConfiguration?.outputFormat?.maxSuggestionsPerReview ?? 5
  );

  // Review mode config
  const [outputMode, setOutputMode] = useState(
    persona?.reviewConfiguration?.outputMode || 'json_only'
  );

  // Validation
  const [errors, setErrors] = useState({});

  // Generate key from name
  useEffect(() => {
    if (!persona && name && !key) {
      const generatedKey = name
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .substring(0, 50);
      setKey(generatedKey);
    }
  }, [name, key, persona]);

  const validateForm = () => {
    const newErrors = {};

    if (!key.trim()) {
      newErrors.key = 'Key is required';
    } else if (!/^[a-z0-9-]+$/.test(key)) {
      newErrors.key = 'Key must be lowercase letters, numbers, and hyphens only';
    }

    if (!name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!systemPrompt.trim()) {
      newErrors.systemPrompt = 'System prompt is required';
    }

    if (dimensions.length === 0) {
      newErrors.dimensions = 'At least one evaluation dimension is required';
    }

    const invalidDimensions = dimensions.filter(d => !d.name.trim());
    if (invalidDimensions.length > 0) {
      newErrors.dimensions = 'All dimensions must have a name';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (!validateForm()) return;

    const reviewerData = {
      key: key.trim(),
      name: name.trim(),
      personaPurpose: 'therapy_reviewer',
      description: description.trim(),
      system_prompt: systemPrompt.trim(),
      modalities: ['text'],
      reviewConfiguration: {
        evaluationDimensions: dimensions.map(d => ({
          name: d.name.trim().toLowerCase().replace(/\s+/g, '_'),
          weight: d.weight,
          description: d.description.trim(),
          scoringCriteria: d.scoringCriteria.trim(),
          maxScore: d.maxScore ?? 3
        })),
        outputFormat: {
          includeTranscriptExcerpts: includeExcerpts,
          maxSuggestionsPerReview: maxSuggestions
        },
        outputMode
      },
      isActive: true
    };

    if (persona?._id) {
      reviewerData._id = persona._id;
    }

    onSave(reviewerData);
  };

  const addDimension = () => {
    setDimensions([...dimensions, { ...DEFAULT_DIMENSION }]);
  };

  const updateDimension = (index, field, value) => {
    const updated = [...dimensions];
    updated[index] = { ...updated[index], [field]: value };
    setDimensions(updated);
  };

  const removeDimension = (index) => {
    if (dimensions.length > 1) {
      setDimensions(dimensions.filter((_, i) => i !== index));
    }
  };

  // Handle AI-generated persona applied to form
  const handleAIApply = (generated) => {
    if (generated.name) setName(generated.name);
    if (generated.key && !persona) setKey(generated.key); // Only set key for new personas
    if (generated.description) setDescription(generated.description);
    if (generated.system_prompt) setSystemPrompt(generated.system_prompt);

    if (generated.reviewConfiguration) {
      const rc = generated.reviewConfiguration;
      if (rc.evaluationDimensions && rc.evaluationDimensions.length > 0) {
        // Map global scoringScale into per-dimension maxScore if dimensions don't have their own
        const globalScale = rc.scoringScale || 3;
        setDimensions(rc.evaluationDimensions.map(d => ({
          ...d,
          maxScore: d.maxScore ?? globalScale
        })));
      }
      if (rc.outputMode) setOutputMode(rc.outputMode);
      if (rc.outputFormat) {
        if (rc.outputFormat.includeTranscriptExcerpts !== undefined) {
          setIncludeExcerpts(rc.outputFormat.includeTranscriptExcerpts);
        }
        if (rc.outputFormat.maxSuggestionsPerReview !== undefined) {
          setMaxSuggestions(rc.outputFormat.maxSuggestionsPerReview);
        }
      }
    }
  };

  // Build current persona state for AI context
  const currentPersonaState = {
    name,
    key,
    description,
    system_prompt: systemPrompt,
    reviewConfiguration: {
      evaluationDimensions: dimensions,
      outputMode,
      outputFormat: {
        includeTranscriptExcerpts: includeExcerpts,
        maxSuggestionsPerReview: maxSuggestions
      }
    }
  };

  return (
    <Box>
      {/* Basic Info Section */}
      <Typography variant="h6" gutterBottom>
        {persona?._id ? 'Edit Reviewer Persona' : persona ? 'Clone Reviewer Persona' : 'Create Reviewer Persona'}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Reviewer personas define modality-specific evaluation criteria for therapy sessions.
        The system prompt should contain detailed instructions for evaluating sessions in a specific
        therapeutic approach (e.g., MBT, DBT, CBT).
      </Typography>

      {Object.keys(errors).length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Please fix the validation errors below
        </Alert>
      )}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Reviewer Key"
            value={key}
            onChange={(e) => setKey(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
            error={!!errors.key}
            helperText={errors.key || 'Unique identifier (e.g., mbt-therapy-reviewer)'}
            placeholder="mbt-therapy-reviewer"
            disabled={!!persona?._id}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            error={!!errors.name}
            helperText={errors.name}
            placeholder="MBT Session Reviewer"
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            multiline
            rows={2}
            placeholder="Evaluates therapy sessions for MBT fidelity and mentalizing quality..."
          />
        </Grid>
      </Grid>

      <Divider sx={{ my: 3 }} />

      {/* System Prompt + AI Assistant Section (two-column layout) */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Typography variant="h6">Review Prompt</Typography>
          <Tooltip title="This prompt defines how the reviewer evaluates sessions. Include modality-specific criteria, red flags to watch for, and expected output format. Use the AI assistant on the right to generate or refine this prompt.">
            <IconButton size="small">
              <HelpIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Define the complete review instructions, or use the AI Prompt Assistant to generate them.
        </Typography>

        <Grid container spacing={2}>
          {/* Left column: System prompt textarea */}
          <Grid item xs={12} md={7}>
            <TextField
              fullWidth
              multiline
              rows={15}
              value={systemPrompt}
              onChange={(e) => setSystemPrompt(e.target.value)}
              error={!!errors.systemPrompt}
              helperText={errors.systemPrompt}
              placeholder="You are an expert [MODALITY] clinical supervisor..."
              sx={{ fontFamily: 'monospace', '& .MuiInputBase-input': { fontFamily: 'monospace' } }}
            />
          </Grid>

          {/* Right column: AI Prompt Assistant */}
          <Grid item xs={12} md={5}>
            <ReviewerPromptAssistant
              currentPersona={currentPersonaState}
              onApply={handleAIApply}
              token={token}
            />
          </Grid>
        </Grid>
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* Evaluation Dimensions Section */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Typography variant="h6">Evaluation Dimensions</Typography>
          <Tooltip title="Define the specific dimensions this reviewer will score. Weights determine importance in the overall quality calculation.">
            <IconButton size="small">
              <HelpIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Configure the scoring dimensions for this reviewer. Each dimension should have a clear name,
          description, and scoring criteria.
        </Typography>

        {errors.dimensions && (
          <Alert severity="error" sx={{ mb: 2 }}>{errors.dimensions}</Alert>
        )}

        {dimensions.map((dim, idx) => (
          <EvaluationDimensionEditor
            key={idx}
            dimension={dim}
            index={idx}
            onUpdate={updateDimension}
            onRemove={removeDimension}
            canRemove={dimensions.length > 1}
          />
        ))}

        <Button
          startIcon={<AddIcon />}
          onClick={addDimension}
          variant="outlined"
          size="small"
          sx={{ mt: 1 }}
        >
          Add Dimension
        </Button>
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* Output Configuration */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom>Output Configuration</Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Output Mode</InputLabel>
              <Select
                value={outputMode}
                onChange={(e) => setOutputMode(e.target.value)}
                label="Output Mode"
              >
                <MenuItem value="json_only">JSON Only (structured scores)</MenuItem>
                <MenuItem value="rich_text">Rich Text (clinical narrative + JSON summary)</MenuItem>
              </Select>
            </FormControl>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {outputMode === 'rich_text'
                ? 'Reviewer produces a natural clinical report with an appended JSON block for scoring.'
                : 'Reviewer outputs rigid JSON with scores and evidence fields.'}
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Scoring scales are configured per dimension using the "Max" field above.
            </Typography>
          </Grid>
        </Grid>
        <Grid container spacing={2} alignItems="center" sx={{ mt: 1 }}>
          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={includeExcerpts}
                  onChange={(e) => setIncludeExcerpts(e.target.checked)}
                />
              }
              label="Include transcript excerpts as evidence"
            />
            <Typography variant="body2" color="text.secondary" sx={{ ml: 4 }}>
              When enabled, the reviewer will include specific transcript excerpts to support scores.
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography gutterBottom>Max suggestions per review: {maxSuggestions}</Typography>
            <Slider
              value={maxSuggestions}
              onChange={(e, v) => setMaxSuggestions(v)}
              min={1}
              max={10}
              marks
              valueLabelDisplay="auto"
            />
          </Grid>
        </Grid>
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* Preview */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" gutterBottom>Configured Dimensions</Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {dimensions.filter(d => d.name).map((dim, idx) => (
            <Chip
              key={idx}
              label={`${dim.name} (${dim.weight}x, 0-${dim.maxScore ?? 3})`}
              size="small"
              color="primary"
              variant="outlined"
            />
          ))}
        </Box>
      </Box>

      {/* Actions */}
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
        <Button
          onClick={onCancel}
          startIcon={<CancelIcon />}
          disabled={isLoading}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          startIcon={<SaveIcon />}
          disabled={isLoading}
        >
          {isLoading ? 'Saving...' : 'Save Reviewer'}
        </Button>
      </Box>
    </Box>
  );
}

export default ReviewerPersonaEditor;
